"use client";

import { Check, Monitor, Moon, Palette, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { type ThemeDefinition, type ThemeMode, THEMES } from "@/themes";
import { useThemeSettings } from "@/themes/client/use-theme-settings";
import { DEFAULT_THEME } from "@/themes/default-theme";
import { effectiveTheme } from "@/themes/effective-theme";
import { useEnabledThemes } from "../brand-provider/app-brand-provider";

const MODE_OPTIONS: readonly { id: ThemeMode; icon: typeof Sun }[] = [
  { id: "light", icon: Sun },
  { id: "dark", icon: Moon },
  { id: "system", icon: Monitor },
];

export function ModeToggle() {
  const t = useTranslations("theme");
  const { mode, setMode } = useThemeSettings();

  return (
    <div className="grid grid-cols-3 gap-1" role="radiogroup" aria-label={t("mode")}>
      {MODE_OPTIONS.map(({ id, icon: Icon }) => (
        <Button key={id} role="radio" aria-checked={mode === id} size="sm" variant={mode === id ? "default" : "outline"} onClick={() => setMode(id)}>
          <Icon /> {t(id)}
        </Button>
      ))}
    </div>
  );
}

interface Props {
  theme: ThemeDefinition;
  selected: boolean;
  onSelect: (id: string) => void;
}

function ThemeSwatch({ theme, selected, onSelect }: Props) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={theme.name}
      title={theme.name}
      onClick={() => onSelect(theme.id)}
      className={cn("relative grid size-8 place-items-center overflow-hidden rounded-full border-2 outline-none focus-visible:ring-2 focus-visible:ring-ring", selected ? "border-foreground" : "border-transparent")}
      style={{ background: `linear-gradient(135deg, ${theme.light.primary} 50%, ${theme.light.brand} 50%)` }}
    >
      {selected && <Check className="size-4 text-white drop-shadow" />}
    </button>
  );
}

/** The colour themes the app offers; nothing at all when only one is on. */
export function ThemeSwatches() {
  const t = useTranslations("theme");
  const { theme, setTheme } = useThemeSettings();
  const enabled = useEnabledThemes();
  if (enabled.length < 2) return null;
  const shown = effectiveTheme(theme, enabled, DEFAULT_THEME);

  return (
    <div className="grid grid-cols-7 gap-2" role="radiogroup" aria-label={t("colour")}>
      {THEMES.filter((th) => enabled.includes(th.id)).map((th) => (
        <ThemeSwatch key={th.id} theme={th} selected={shown === th.id} onSelect={setTheme} />
      ))}
    </div>
  );
}

export function ThemePicker() {
  const t = useTranslations("theme");
  const several = useEnabledThemes().length > 1;

  return (
    <Popover>
      <PopoverTrigger render={<Button variant="ghost" size="icon" aria-label={t("title")} />}>
        <Palette />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-4">
        <p className="text-sm font-medium">{t("mode")}</p>
        <ModeToggle />
        {several && <p className="text-sm font-medium">{t("colour")}</p>}
        <ThemeSwatches />
      </PopoverContent>
    </Popover>
  );
}
